import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHydraUIAPIQueryOptions, hydraUiApi } from '@/services/api';
import type { CdpOutResult } from '@/services/auth/types';
import { COMMAND_DIALOG_QUERY_DEFAULTS } from './constants';
import { mapCdpOutToSearchResult, matchesCdpOut } from './utils/integration-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseCdpOutSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type CdpOutSource = SearchSource<CdpOutResult>;

export function useCdpOutSource({ workspaceId, enabled, query }: UseCdpOutSourceOptions): CdpOutSource {
  const cdpOutQuery = useQuery(
    getHydraUIAPIQueryOptions({
      queryKey: ['getCdpOut', { params: { environment_id: workspaceId } }],
      queryFn: ({ signal }) =>
        hydraUiApi.integrations.getCdpOut({
          params: { path: { environment_id: String(workspaceId) } },
          signal,
        }),
      queryConfig: {
        enabled,
        ...COMMAND_DIALOG_QUERY_DEFAULTS,
        // 404 means no CDP Out row — treat as empty, not an error.
        throwOnError: false,
        retry: false,
        select: (data) => data.cdp_out,
      },
    }),
  );

  const results = useMemo(() => {
    const row = cdpOutQuery.data;
    if (row == null || !matchesCdpOut(query)) return [];
    return [mapCdpOutToSearchResult(row, workspaceId)];
  }, [cdpOutQuery.data, query, workspaceId]);

  return { ...cdpOutQuery, results };
}
